import React, { useEffect, useState } from 'react';
import ConfirmationModal from "../components/ConfirmationModal";
import { TableActionColumnHeader } from "../components/TableShell";
import ShieldCareSectionTabs, { SHIELD_CARE_TAB_PHARMACIES } from "../components/ShieldCareSectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import { IconEdit, IconDelete, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import PhoneInput from "../components/PhoneInput";
import { formatPhoneDisplay } from "../shared/utils/phoneFormat";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../shared/utils/entityListSelection";
import ImportCsvPanel from "../components/entity/ImportCsvPanel";
import { parseCsvTextToNormalizedTable, findCsvColumnIndex } from "../shared/utils/csvParse";
import { attachValidatedCsvFileImport, downloadCsvTextFile } from "../shared/utils/csvFileHelpers";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const ErrorBanner = require('../components/ErrorBanner').default;

function ShieldCarePharmacies() {
  const [currentObjs, setCurrentObjs] = useState([]);
  const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
  const account_id = userInfo.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [_id, setID] = useState(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [edit, setEdit] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [formExpanded, setFormExpanded] = useState(false);
  const [importPanelExpanded, setImportPanelExpanded] = useState(false);
  const [importing, setImporting] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm", // "confirm" | "alert"
    onConfirm: null,
  });

  useEffect(() => {
    getDocument();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only fetch
  }, []);

  function getDocument() {
    if (!account_id) return;
    apiFetch(serverUrl + '/getPharmacies', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setCurrentObjs(Array.isArray(data) ? data : []))
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function createDocument() {
    apiFetch(serverUrl + '/createPharmacy', {
      method: 'POST',
      body: JSON.stringify({ account_id, name, address, phone }),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Pharmacy", message: data, mode: "alert", onConfirm: null });
        getDocument();
        collapseForm();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function updateDocument() {
    apiFetch(serverUrl + '/updatePharmacy', {
      method: 'POST',
      body: JSON.stringify({ account_id, _id, name, address, phone }),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Pharmacy", message: data, mode: "alert", onConfirm: null });
        getDocument();
        collapseForm();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  function deleteDocument(currentObj) {
    const { _id: id } = currentObj;
    apiFetch(serverUrl + '/deletePharmacy', {
      method: 'POST',
      body: JSON.stringify({ account_id, _id: id }),
    })
      .then((response) => response.text())
      .then((data) => {
        setConfirmDialog({ open: true, title: "Pharmacy", message: data, mode: "alert", onConfirm: null });
        const newSelectedIds = new Set(selectedIds);
        newSelectedIds.delete(id);
        setSelectedIds(newSelectedIds);
        getDocument();
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  const createUpdate = (e) => {
    e.preventDefault();
    const nameVal = (name != null ? String(name) : '').trim();
    if (!nameVal) {
      setConfirmDialog({ open: true, title: "Pharmacy", message: "Name is required.", mode: "alert", onConfirm: null });
      return;
    }
    if (edit) updateDocument();
    else createDocument();
  };

  const collapseForm = () => {
    setFormExpanded(false);
    setID(null);
    setName('');
    setAddress('');
    setPhone('');
    setEdit(false);
  };

  const fillForm = (obj) => {
    setID(obj._id);
    setName(obj.name || '');
    setAddress(obj.address || '');
    setPhone(obj.phone || '');
    setEdit(true);
    setFormExpanded(true);
  };

  function parseCSV(csvText) {
    const { headers, rows } = parseCsvTextToNormalizedTable(csvText);
    const nameIndex = findCsvColumnIndex(headers, ['name', 'pharmacy']);
    const addressIndex = findCsvColumnIndex(headers, ['address']);
    const phoneIndex = findCsvColumnIndex(headers, ['phone']);
    if (nameIndex === -1) {
      throw new Error('CSV must contain a name (or pharmacy) column');
    }
    const records = [];
    for (const values of rows) {
      if (values.length > nameIndex && (values[nameIndex] || '').trim()) {
        records.push({
          name: values[nameIndex] || '',
          address: addressIndex >= 0 ? (values[addressIndex] || '') : '',
          phone: phoneIndex >= 0 ? (values[phoneIndex] || '') : '',
        });
      }
    }
    return records;
  }

  async function importFromCSV(file) {
    if (!file) {
      setConfirmDialog({ open: true, title: "Import", message: "Please select a CSV file", mode: "alert", onConfirm: null });
      return;
    }
    const text = await file.text();
    await importFromCsvText(text);
  }

  async function importFromCsvText(text) {
    setImporting(true);
    try {
      const records = parseCSV(text);
      if (records.length === 0) {
        setConfirmDialog({ open: true, title: "Import", message: "No valid records found in CSV file", mode: "alert", onConfirm: null });
        setImporting(false);
        return;
      }
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        if (!(record.name && String(record.name).trim())) {
          errorCount++;
          errors.push(`Row ${i + 2}: Name is required`);
          continue;
        }
        try {
          const response = await apiFetch(serverUrl + '/createPharmacy', {
            method: 'POST',
            body: JSON.stringify({
              account_id,
              name: String(record.name).trim(),
              address: String(record.address || '').trim(),
              phone: String(record.phone || '').trim(),
            }),
          });
          const data = await response.text();
          if (response.ok) successCount++;
          else {
            errorCount++;
            errors.push(`Row ${i + 2}: ${data}`);
          }
        } catch (error) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${getMessageForFetchError(error)}`);
        }
      }
      let message = `Import completed!\nSuccess: ${successCount}\nErrors: ${errorCount}`;
      if (errors.length > 0 && errors.length <= 10) {
        message += '\n\nErrors:\n' + errors.join('\n');
      } else if (errors.length > 10) {
        message += '\n\nFirst 10 errors:\n' + errors.slice(0, 10).join('\n');
      }
      setConfirmDialog({ open: true, title: "Import completed", message, mode: "alert", onConfirm: null });
      getDocument();
    } catch (error) {
      setBannerError(getMessageForFetchError(error, 'Error importing CSV.'));
    } finally {
      setImporting(false);
    }
  }

  function handleFileChange(e) {
    attachValidatedCsvFileImport(e, importFromCSV, (msg) => {
      setConfirmDialog({ open: true, title: "Import", message: msg, mode: "alert", onConfirm: null });
    });
  }

  function downloadSampleCSV() {
    downloadCsvTextFile('pharmacies-sample.csv', [
      'name,address,phone',
      'CVS Main,100 Main St,555-1000',
      'Walgreens Oak,456 Oak Ave,555-1001',
    ]);
  }

  function handleSort(key) {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  }

  function getFilteredAndSortedData() {
    let filtered = (currentObjs || []).filter((obj) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (obj.name && obj.name.toLowerCase().includes(searchLower)) ||
        (obj.address && obj.address.toLowerCase().includes(searchLower)) ||
        (obj.phone && String(obj.phone).includes(searchTerm))
      );
    });
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortConfig.key] ?? '';
        const bValue = b[sortConfig.key] ?? '';
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, getFilteredAndSortedData());
  }

  function handleSelectAll(e) {
    const filteredData = getFilteredAndSortedData();
    const newSelectedIds = new Set(selectedIds);
    if (e.target.checked) filteredData.forEach((obj) => newSelectedIds.add(obj._id));
    else filteredData.forEach((obj) => newSelectedIds.delete(obj._id));
    setSelectedIds(newSelectedIds);
  }

  function handleSelectOne(id, checked) {
    const newSelectedIds = new Set(selectedIds);
    if (checked) newSelectedIds.add(id);
    else newSelectedIds.delete(id);
    setSelectedIds(newSelectedIds);
  }

  async function deleteSelected() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({ open: true, title: "Delete pharmacies", message: "Please select at least one record to delete", mode: "alert", onConfirm: null });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete pharmacies",
      message: `Are you sure you want to delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        let successCount = 0;
        let errorCount = 0;
        for (const id of idsToDelete) {
          try {
            const response = await apiFetch(serverUrl + '/deletePharmacy', {
              method: 'POST',
              body: JSON.stringify({ account_id, _id: id }),
            });
            if (response.ok) successCount++;
            else errorCount++;
          } catch {
            errorCount++;
          }
        }
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: `Delete completed! Success: ${successCount}, Errors: ${errorCount}`,
          onConfirm: () => {
            setSelectedIds(new Set());
            getDocument();
            setDeleting(false);
          },
        });
      },
    });
  }

  const allFieldsFilled = Boolean(String(name ?? '').trim());

  const filteredData = getFilteredAndSortedData();
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, filteredData);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  return (
    <div className="page-container page-container-full page-container--section-tabs">
      <SectionTabsPageHeader subtitle="Manage pharmacies and their details.">
        <ShieldCareSectionTabs activeTab={SHIELD_CARE_TAB_PHARMACIES} />
      </SectionTabsPageHeader>
      <div className="page-content">
        <div className="page-top">
          <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />
          <ConfirmationModal
            open={confirmDialog.open}
            title={confirmDialog.title || "Confirm"}
            message={confirmDialog.message}
            mode={confirmDialog.mode === "alert" ? "alert" : "confirm"}
            confirmLabel={confirmDialog.mode === "alert" ? "OK" : undefined}
            onConfirm={() => {
              const fn = confirmDialog.onConfirm;
              setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
              if (fn) fn();
            }}
            onCancel={() =>
              setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null })
            }
          />
          <ImportCsvPanel
            expanded={importPanelExpanded}
            fileInputId="pharmacies-csv-import"
            importing={importing}
            columnHint="Columns: name (required), address, phone (header row required)"
            onDownloadSample={downloadSampleCSV}
            onFileChange={handleFileChange}
            onImportText={importFromCsvText}
          />
          {formExpanded && (
            <div className="panel">
              <div className="panel-title">
                {edit ? 'Edit pharmacy' : 'Add new pharmacy'}{' '}
                <span className="panel-required-hint">(Name required)</span>
              </div>
              <div className="panel-row">
                <input type="text" value={name ?? ''} placeholder="Name *" onChange={(e) => setName(e.target.value)} className="input input-min-140" />
                <input type="text" value={address ?? ''} placeholder="Address" onChange={(e) => setAddress(e.target.value)} className="input input-min-200" />
                <PhoneInput value={phone ?? ''} onChange={setPhone} placeholder="Phone" className="input input-min-120" />
                <button type="button" onClick={(e) => createUpdate(e)} disabled={!allFieldsFilled} className="btn btn-primary">{edit ? 'Update' : 'Create'}</button>
                {edit && <button type="button" onClick={collapseForm} className="btn btn-secondary">Cancel</button>}
              </div>
            </div>
          )}
          <div className="toolbar">
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search name, address, phone…" className="input input-search-260" />
            <button type="button" onClick={() => setSearchTerm('')} className="btn btn-secondary btn-sm">Clear</button>
            <span className="toolbar-divider" />
            <button type="button" onClick={() => { setImportPanelExpanded(!importPanelExpanded); if (!importPanelExpanded) setFormExpanded(false); }} className={`btn btn-md ${importPanelExpanded ? 'btn-primary-outline' : 'btn-primary'}`}>{importPanelExpanded ? '− Import CSV' : '+ Import CSV'}</button>
            <button type="button" onClick={() => { if (formExpanded) collapseForm(); else setFormExpanded(true); }} className={`btn btn-md ${formExpanded ? 'btn-primary-outline' : 'btn-primary'}`}>{formExpanded ? '− New' : '+ New'}</button>
            <button type="button" onClick={deleteSelected} disabled={selectedVisibleCount === 0 || deleting} className="btn btn-danger">Delete{selectedCountSuffix}</button>
          </div>
        </div>
        <div className="table-scroll">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>
                    <input type="checkbox" checked={filteredData.length > 0 && filteredData.every((obj) => selectedIds.has(obj._id))} onChange={handleSelectAll} disabled={filteredData.length === 0} />
                  </th>
                  <th className="sortable" onClick={() => handleSort('name')}>Name</th>
                  <th>Address</th>
                  <th>Phone</th>
                  <TableActionColumnHeader align="left" />
                </tr>
              </thead>
              <tbody>
                {filteredData.map((currentObj) => (
                  <tr key={currentObj._id}>
                    <td>
                      <input type="checkbox" checked={selectedIds.has(currentObj._id)} onChange={(e) => handleSelectOne(currentObj._id, e.target.checked)} />
                    </td>
                    <td className="table-cell-ellipsis">{currentObj.name || '—'}</td>
                    <td className="table-cell-ellipsis" title={currentObj.address}>{currentObj.address || '—'}</td>
                    <td>{formatPhoneDisplay(currentObj.phone)}</td>
                    <td>
                      <RowActionIcons>
                        <button type="button" onClick={() => fillForm(currentObj)} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                        <button type="button" onClick={() => deleteDocument(currentObj)} className="btn-link-danger" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                      </RowActionIcons>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShieldCarePharmacies;
